import React from "react";
import moment from "moment";
import axios from "axios";
import * as Icon from "ubs-icons";
import * as htmlToImage from "html-to-image";
import { isArray, isString, isObject, isNumber, isEqual } from "lodash";
import cloneDeep from "lodash/cloneDeep";
import { isAfter, isBefore } from "date-fns";
import { minutesToHm, minutesTo12Hour, localToIST, dateFormatter, hourToMinute } from "./date";
import { isInApp } from "./device";
import { getKeyName } from "./selector";
import { getName } from "./string";
import { getCustomFieldValue } from "./common";
import { Encryption, getCookie } from "./storage";
import {
    APP,
    COLOR,
    HRMS_STATUS,
    CONSTANT,
    PAGINATION,
    CUSTOM_FIELD_TYPE,
    REGEX,
    D_FIELDS,
    OPERATORS,
    ALLOCATION,
    STATUS_OPTIONS,
    BANK_INFO_COUNTRY,
    BASIC_INFO_ACTIVITY_KEY,
    DOCUMENT_WATERMARK
} from "../constant";

/**
 * HRMS-specific helpers relocated from hrms/src/common/functions/common.jsx.
 * Re-exported from ubs-framework/v2/common/function via index.jsx.
 */

// Minimal local copies of HRMS route keys used by checkRoute / checkBasicInfo.
const ROUTES = { myProfile: "my-profile", employeeProfile: "employees" };


const BLOOD_GROUP_LIST = [
    {value: 1, label: 'O-'},
    {value: 2, label: 'O+'},
    {value: 3, label: 'A-'},
    {value: 4, label: 'A+'},
    {value: 5, label: 'B-'},
    {value: 6, label: 'B+'},
    {value: 7, label: 'AB-'},
    {value: 8, label: 'AB+'},
    {value: 9, label: 'A1+'},
    {value: 10, label: 'A1-'},
    {value: 11, label: 'A2+'},
    {value: 12, label: 'A2-'},
];

// HRMS API axios instance (mirrors hrms/src/store/actions/index.jsx).
const api = axios.create();
api.defaults.baseURL = process.env.REACT_APP_HRMS_API_URL;

const logEnable = false;

// HRMS getFixed(number, precision) semantics (framework's getFixed differs), used by getPlural.
export const roundFixed = (number = 0, precision = 3) => {
    const factor = Math.pow(10, precision);
    return Math.round(number * factor) / factor;
}

const HEX_COLOR_REGEX = /^#[0-9a-fA-F]{6}$/;

const clampNumber = (value, min, max, fallback) => {
    const number = Number(value);
    return Number.isFinite(number) ? Math.min(Math.max(number, min), max) : fallback;
};

// monthDiff
export const monthDiff = (startdate, enddate) => {
    // return d2?.getMonth() - d1?.getMonth() +  (12 * (d2?.getFullYear() - d1?.getFullYear()))
    let startdateMoment = moment(startdate).set({hour: 0, minute: 0, second: 0, millisecond: 0})
    let enddateMoment = moment(enddate).set({hour: 23, minute: 59, second: 59, millisecond: 999}).add(1, 'second')

    if (startdateMoment.isValid() === true && enddateMoment.isValid() === true) {
        //getting the difference in years
        let years = enddateMoment.diff(startdateMoment, 'years');
        //moment returns the total months between the two dates, subtracting the years
        startdateMoment.add(years, 'years')
        let months = enddateMoment.diff(startdateMoment, 'months');
        if (years > 0) months += (years * 12)
        return months;
    }
}

// diffInMinute
export function diffInMinute(start, end) {
    /**
     * start and end must be in HH:MM format and return total diffrence in minutes
     * */
    if (start && end) {
        let LocalStart = hourToMinute(start)
        let localEnd = hourToMinute(end);
        if (localEnd >= LocalStart) {
            return localEnd - LocalStart
        } else 0
    } else return 0
}

// secondsToHs
export const secondsToHs = (d, flag) => {
    d = Number(d);
    let h = Math.floor(d / 3600);
    let m = Math.floor(d % 3600 / 60);
    let s = Math.floor(d % 3600 % 60);
    if (h < 10) h = '0' + h;
    if (m < 10) m = '0' + m;
    if (s < 10) s = '0' + s;
    if (flag) {
        if(s > 0) return `${h} H : ${m} M : ${s} S`;
        else return `${h} H : ${m} M`;
    }
    else return `${h}:${m}:${s}`;
}

// dateDiff
export const dateDiff = (d1, d2) => {
    const Difference_In_Time = new Date(d1)?.getTime() - new Date(d2)?.getTime();
    // To calculate the no. of days between two dates
    return Difference_In_Time / (1000 * 3600 * 24)
}

// getDateByTime
export const getDateByTime = (time, format) => {
    let date = time.split(':')
    if (date?.length > 1 && format) {
        return moment().set({hour: date[0], minute: date[1]}).format(format);
    } else if (date?.length > 1) {
        return moment().set({hour: date[0], minute: date[1]})
    }
}

// sameDay
export const sameDay = (d1, d2) => {
    const date1 = new Date(d1)
    const date2 = new Date(d2)
    return date1.getFullYear() === date2.getFullYear() &&
        date1.getMonth() === date2.getMonth() &&
        date1.getDate() === date2.getDate();
}

// getDaysArray
export const getDaysArray = (date1, date2) => {
    const singleDay = 24 * 60 * 60 * 1000
    const length = Math.ceil((new Date(date2).getTime() - new Date(date1).getTime()) / singleDay)
    return (new Array(length)).fill('').map((v, i) => new Date(date1 + (i * singleDay)));
}

// getDatesBetweenDates
export const getDatesBetweenDates = (startDate, endDate) => {
    const rangeEnd = endDate || startDate;
    let dates = [];
    let currentDate = moment(startDate).startOf('day');
    const endDateMoment = moment(rangeEnd).startOf('day');

    while (currentDate.isSameOrBefore(endDateMoment, 'day')) {
        dates.push(currentDate.valueOf());
        currentDate = currentDate.clone().add(1, 'day');
    }

    return dates;
}

// getISTDatesBetweenDates
export const getISTDatesBetweenDates = (startDate, endDate) => {
    const rangeEnd = endDate || startDate;
    let dates = [];
    let currentDate = moment(startDate).startOf('day');
    const endDateMoment = moment(rangeEnd).startOf('day');

    while (currentDate.isSameOrBefore(endDateMoment, 'day')) {
        dates.push(localToIST(currentDate.valueOf()));
        currentDate = currentDate.clone().add(1, 'day');
    }

    return dates;
}
// calculateAge
export const calculateAge = (dobIso, untilIso = new Date()) => {
    const dob = new Date(dobIso);
    const until = new Date(untilIso);

    let age = until?.getFullYear() - dob?.getFullYear();

    const hasHadBirthday =
      until?.getMonth() > dob?.getMonth() ||
      (until?.getMonth() === dob?.getMonth() && until?.getDate() >= dob?.getDate());

    if (!hasHadBirthday) {
      age--;
    }

    return age || 0;
};

// calculateTimeDifference
export const calculateTimeDifference = (startTime, endTime) => {
    // Initialize default return object
    const defaultReturn = {
        hours: 0,
        minutes: 0,
        seconds: 0,
        totalSeconds: 0,
        formatted: '--:--:--',
        isValid: false
    };

    // Validate inputs
    if (!startTime || !endTime) {
        return defaultReturn;
    }

    try {
        // Convert to Date objects if needed
        const start = startTime instanceof Date ? startTime : new Date(startTime);
        const end = endTime instanceof Date ? endTime : new Date(endTime);

        // Check if dates are valid
        if (isNaN(start.getTime()) || isNaN(end.getTime())) {
            return defaultReturn;
        }

        // Calculate difference in milliseconds
        const diffMs = end.getTime() - start.getTime();

        // Handle negative difference (end time before start time)
        if (diffMs < 0) {
            return defaultReturn;
        }

        // Convert to seconds, minutes, hours
        const totalSeconds = Math.floor(diffMs / 1000);
        const hours = Math.floor(totalSeconds / 3600);
        const minutes = Math.floor((totalSeconds % 3600) / 60);
        const seconds = totalSeconds % 60;

        // Format with leading zeros and labels
        const formatNumber = (num) => String(num).padStart(2, '0');
        const formatted = `${formatNumber(hours)} H : ${formatNumber(minutes)} M`;

        return {
            hours,
            minutes,
            seconds,
            totalSeconds,
            formatted,
            isValid: true
        };
    } catch (error) {
        console.error('Error calculating time difference:', error);
        return defaultReturn;
    }

}
/**
 * HRMS/Recruit Documents — watermark payload/hydration normalization.
 * The watermark itself is rendered ONLY in the generated PDF by the PDF server
 * (pdf-node/services/HRMS/documents/utlis.js -> buildWatermarkHtml).
 * Enum-ish fields are numeric codes (DOCUMENT_WATERMARK.VALUES, single source of
 * truth); an invalid/missing value falls back to the field default.
 */
const watermarkEnumValue = (field, value, valuesMap) => {
    return Object.values(valuesMap).includes(value) ? value : DOCUMENT_WATERMARK.DEFAULT[field];
};

export const normalizeWatermark = (watermark) => {
    const base = {...DOCUMENT_WATERMARK.DEFAULT, ...(watermark || {})};
    return {
        isActive: !!base.isActive,
        type: watermarkEnumValue('type', base.type, DOCUMENT_WATERMARK.VALUES.TYPE),
        text: (base.text || '').slice(0, DOCUMENT_WATERMARK.TEXT_MAX_LENGTH),
        image: base.image?.url ? {fileId: base.image.fileId || null, url: base.image.url} : null,
        layout: watermarkEnumValue('layout', base.layout, DOCUMENT_WATERMARK.VALUES.LAYOUT),
        opacity: clampNumber(base.opacity, DOCUMENT_WATERMARK.OPACITY.MIN, DOCUMENT_WATERMARK.OPACITY.MAX, DOCUMENT_WATERMARK.DEFAULT.opacity),
        size: clampNumber(base.size, DOCUMENT_WATERMARK.SIZE.MIN, DOCUMENT_WATERMARK.SIZE.MAX, DOCUMENT_WATERMARK.DEFAULT.size),
        rotation: clampNumber(base.rotation, DOCUMENT_WATERMARK.ROTATION.MIN, DOCUMENT_WATERMARK.ROTATION.MAX, DOCUMENT_WATERMARK.DEFAULT.rotation),
        rotationDirection: watermarkEnumValue('rotationDirection', base.rotationDirection, DOCUMENT_WATERMARK.VALUES.ROTATION_DIRECTION),
        color: HEX_COLOR_REGEX.test(base.color) ? base.color : DOCUMENT_WATERMARK.DEFAULT.color,
        thickness: watermarkEnumValue('thickness', base.thickness, DOCUMENT_WATERMARK.VALUES.THICKNESS),
        fontFamily: watermarkEnumValue('fontFamily', base.fontFamily, DOCUMENT_WATERMARK.VALUES.FONT_FAMILY),
        position: watermarkEnumValue('position', base.position, DOCUMENT_WATERMARK.VALUES.POSITION),
        offsetX: clampNumber(base.offsetX, DOCUMENT_WATERMARK.OFFSET.MIN, DOCUMENT_WATERMARK.OFFSET.MAX, 0),
        offsetY: clampNumber(base.offsetY, DOCUMENT_WATERMARK.OFFSET.MIN, DOCUMENT_WATERMARK.OFFSET.MAX, 0),
    };
};

// durationCalc
export const durationCalc = (enddate, startdate) => {
    //define moments for the startdate and enddate

    let startdateMoment = moment(startdate).set({hour: 0, minute: 0, second: 0, millisecond: 0})
    let enddateMoment = moment(enddate).set({hour: 23, minute: 59, second: 59, millisecond: 999}).add(1, 'second')

    if (startdateMoment.isValid() === true && enddateMoment.isValid() === true) {
        //getting the difference in years
        let years = enddateMoment.diff(startdateMoment, 'years');
        //moment returns the total months between the two dates, subtracting the years
        startdateMoment.add(years, 'years')
        let months = enddateMoment.diff(startdateMoment, 'months');

        //to calculate the days, first get the previous month and then subtract it
        startdateMoment.add(months, 'months');
        let days = enddateMoment.diff(startdateMoment, 'days')

        let timing = ''

        if (years) timing += `${years} Year `
        if (months) timing += `${months} ${months > 1 ? 'Months ' : 'Month '}`
        if (days) timing += `${days} ${days > 1 ? 'Days ' : 'Day '}`
        return timing;
    } else {
        return undefined;
    }
}

// generateYears
export const generateYears = (prev = 5, next = 1, year) => {
    const years = []
    if (year && year >= 2020) prev = new Date().getFullYear() - year + 1
    for (let i = 1; i <= next; i++) {
        const y = new Date().getFullYear() + (i)
        years.push({label: y, value: y});
    }
    for (let i = 0; i < prev; i++) {
        const y = new Date().getFullYear() - (i)
        years.push({label: y, value: y});
    }
    return years
}

// Format audit custom fields using the common formatter, with names for selection IDs.
export const getCustomFieldLogValue = (value, field = {}, users = {}) => {
    if (value == null || value === '') return '--';

    let type = Number(field?.type);
    if (type === CUSTOM_FIELD_TYPE.TEXT || type === CUSTOM_FIELD_TYPE.TEXT_AREA) {
        return getCustomFieldValue(String(value), {...field, type});
    }

    const isUserField = type === CUSTOM_FIELD_TYPE.SINGLE_USER_SELECT || type === CUSTOM_FIELD_TYPE.MULTI_USER_SELECT;
    if (type === CUSTOM_FIELD_TYPE.SINGLE_USER_SELECT) type = CUSTOM_FIELD_TYPE.SELECT;
    if (type === CUSTOM_FIELD_TYPE.MULTI_USER_SELECT) type = CUSTOM_FIELD_TYPE.MULTI_SELECT;

    if (![CUSTOM_FIELD_TYPE.SELECT, CUSTOM_FIELD_TYPE.RADIO, CUSTOM_FIELD_TYPE.MULTI_SELECT].includes(type)) {
        return getCustomFieldValue(value, {...field, type});
    }

    const options = (field?.options || []).map(option => ({
        _id: String(option?._id ?? option?.value ?? option),
        value: option?.label ?? option?.name ?? option?.value ?? option,
    }));
    const values = (Array.isArray(value) ? value : [value]).map(item => {
        if (item == null || item === '') return '--';

        const id = String(item?._id ?? item?.value ?? item?.user ?? item);
        const option = options.find(option => option._id === id || option.value === item);
        if (option) return option._id;

        const label = item?.label || item?.name || getName(item)?.trim() || item?.displayName;
        const user = isUserField ? users[id] : null;
        const userName = getName(user)?.trim() || user?.label || user?.displayName;
        const displayValue = typeof item === 'string' && !REGEX.OBJECT_ID.test(item) && !Number.isFinite(Number(item)) ? item : null;
        options.push({_id: id, value: label || userName || displayValue || 'Unavailable value'});
        return id;
    });

    const customValue = type === CUSTOM_FIELD_TYPE.MULTI_SELECT ? values : values[0];
    return getCustomFieldValue(customValue, {...field, options, type});
};

// getCustomFieldMinDate
export const getCustomFieldMinDate = (validations, defaultDate) => {
    const minYear = validations?.minYear;
    return (minYear != null && minYear !== '') ? moment().subtract(Number(minYear), 'years').toDate() : defaultDate;
};

// getCustomFieldMaxDate
export const getCustomFieldMaxDate = (validations, defaultDate) => {
    const maxYear = validations?.maxYear;
    return (maxYear != null && maxYear !== '') ? moment().add(Number(maxYear), 'years').toDate() : defaultDate;
};

// totalRecords
export const totalRecords = (length) => {
    return (length > 0 && length < 10) ? `0${length}` : length;
}

// getPlural
export const getPlural = (prefix, value = 0) => {
    return `${roundFixed(value, 3)} ${prefix}${value > 1 ? 's' : ''}`;
}

// monthsBetween
export const monthsBetween = (...args) => {
    let [a, b] = args.map(arg => arg.split("-").slice(0, 2).reduce((y, m) => m - 1 + y * 12));
    return Array.from({length: b - a + 1}, _ => a++).map(m => ~~(m / 12) + "-" + ("0" + (m % 12 + 1)).slice(-2) + "-01");
}

// ordinalSuffixOf
export function ordinalSuffixOf(i) {
    let j = i % 10, k = i % 100;
    if (j === 1 && k !== 11) return "st";
    if (j === 2 && k !== 12) return "nd";
    if (j === 3 && k !== 13) return "rd";
    return "th";
}

// createArrayWithNumbers
export function createArrayWithNumbers(length) {
    return Array.from({length}, (_, k) => k + 1);
}

// getPaginateWithSearchList
export const getPaginateWithSearchList = ({list = [], currentPage = PAGINATION.currentPage, limit = PAGINATION.limit, search = ''}) => {
    list = list.filter(data => {
        const pattern = new RegExp(search?.toLowerCase());
        return !!data?.name?.toLowerCase()?.match(pattern);
    });
    return list.slice((currentPage - 1) * limit, currentPage * limit)
}

// setNestedValue
export const setNestedValue = (obj, path, key, value) => {
    const pathParts = path?.split(".")
    let current = obj
    // Navigate to the deepest level specified by path
    for (const part of pathParts) {
        if (!current[part]) {
            current[part] = {}
        }
        current = current[part]
    }
    // Set the value at the key
    current[key] = value || null;
}

// parseIfJson
export const parseIfJson = (data) => {
    try {
        // If it's already an object or array, return true
        if (typeof data === 'object' && data !== null) {
            return data;
        }

        // If it's a string, try parsing
        const parsed = JSON.parse(data);
        return (typeof parsed === 'object' && parsed !== null) ? parsed : null;
    } catch (error) {
        return null;
    }
};

// isValueTruthy
export const isValueTruthy = (value) => {
    if (value === null || value === undefined) return false;
    if (isString(value)) return value.trim().length > 0;
    if (isArray(value)) return value.length > 0;
    if (value instanceof Date) return !isNaN(value.getTime()); // ✅ Check for valid Date
    if (isObject(value)) return Object.keys(value).length > 0;
    if (typeof value === 'boolean') return value === true;
    if (isNumber(value)) return value !== 0;

    return Boolean(value);
};

// htmlToDataURL
export const htmlToDataURL = (content) => {
    return new Promise((resolve, reject) => {
        let iframe = document.createElement('iframe');
        document.body.appendChild(iframe);
        let iframeDoc = iframe.contentDocument || iframe.contentWindow.document;
        iframeDoc.body.innerHTML = content;
        htmlToImage.toPng(iframeDoc.body, {width: 1000, height: 900})
            .then(function (dataUrl) {
                resolve(dataUrl)
            })
            .catch(function (error) {
                resolve('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAH0AAACmCAIAAAB4GxeeAAAAAXNSR0IArs4c6QAAAARnQU1BAACxjwv8YQUAAAAJcEhZcwAADsMAAA7DAcdvqGQAAAFkSURBVHhe7dAxAQAADMOg+TfdyeAJErhF6N3o3ejd6N3o3ejd6N3o3ejd6N3o3ejd6N3o3ejd6N3o3ejd6N3o3ejd6N3o3ejd6N3o3ejd6N3o3ejd6N3o3ejd6N3o3ejd6N3o3ejd6N3o3ejd6N3o3ejd6N3o3ejd6N3o3ejd6N3o3ejd6N3o3ejd6N3o3ejd6N3o3ejd6N3o3ejd6N3o3ejd6N3o3ejd6N3o3ejd6N3o3ejd6N3o3ejd6N3o3ejd6N3o3ejd6N3o3ejd6N3o3ejd6N3o3ejd6N3o3ejd6N3o3ejd6N3o3ejd6N3o3ejd6N3o3ejd6N3o3ejd6N3o3ejd6N3o3ejd6N3o3ejd6N3o3ejd6N3o3ejd6N3o3ejd6N3o3ejd6N3o3ejd6N3o3ejd6N3o3ejd6N3o3ejd6N3o3ejd6N3o3ejd6N3o3ejd6N3o3ejd6N3o3ejd6N3o3ehd2B5QkEUFx84t2QAAAABJRU5ErkJggg==')
                console.error('oops, something went wrong in html to image!', error);
            });
    })
}

// uploadHRMSFile
export const uploadHRMSFile = ({
                                   event = '',
                                   file = [],
                                   data = {},
                                   socketId = '',
                                   orgId = '',
                                   onUploadProgress = () => {
                                   },
                                   route = '/hrmsFileUpload',
                                   encrypt = true,
                                   onSuccess = () => {
                                   },
                                   bodyData = {}
                               }) => {
    return new Promise((resolve, reject) => {
        const payload = {}
        const fileFormData = []
        if (!event) resolve({error: true, message: 'Event is required'})
        for (let i = 0; i < file.length; i++) {
            let e = file[i]
            const rId = e?.rId || Math.random().toString(36).substr(2, 9).toString();
            if (e?.rId) e = e.obj
            const lastModified = e?.lastModified || new Date().getTime();
            // Create a stable file reference to prevent ERR_UPLOAD_FILE_CHANGED
            const stableFile = new File([e], e.name, {
                type: e.type,
                lastModified: lastModified
            });
            payload[rId] = {
                size: e?.size,
                fileName: e?.name,
                mimeType: e?.type,
                lastModified: lastModified,
            };
            fileFormData.push({key: rId, file: stableFile});

        }const body = {
            event,
            "data": {
                ...(file.length && {payload}),
                ...data
            },
            orgId,
            socketId,
            ...((typeof bodyData === 'object' && bodyData !== null) && bodyData)
        }
        console.log(route, ' body', body, fileFormData)
        const formData = new FormData();
        formData.append('body', encrypt ? Encryption(body) : body);
        if (fileFormData?.length) {
            fileFormData.forEach(e => {
                formData.append(e.key, e.file);
            });
        }
        formData.append('finish', true);
        const config = {
            headers: {'Authorization': `Bearer ${getCookie('ubs_token')}`},
            onUploadProgress: e => {
                onUploadProgress(parseInt(e.loaded / e.total * 100));
            },
        };
        api.post(route, formData, config).then(res => {
            console.log(`Files uploaded: `, res?.data)
            resolve(res)
        }).catch((err) => {
            console.log(`Error in file upload: (${event}) ` + err?.message)
            resolve({error: true, message: 'Failed to upload file'})
        })
    })
}

// openLinks
export const openLinks = (link) => {
    if (isInApp()) window.location.href = link
    else window.open(link, '_blank')
}
// randomColor
export const randomColor = () => {
    return CONSTANT.COLORS[Math.floor(Math.random() * CONSTANT.COLORS.length)];
};

// ProgressPercentage
export const ProgressPercentage = React.memo((props) => {
        return new Array(10).fill(1).map((_, i) => {
            return <span key={i}
                         className={`u_wd-activity ${props?.noActivity ? 'u_bg-pastelred' : props?.progressCount > i ? props?.progressCount <= 4 ? 'u_bg-green' : (props?.progressCount > 4 && props?.progressCount < 8) ? 'u_bg-green' : 'u_bg-green' : ''} `}/>
        })
    }
)

// sendMessageRNW
export const sendMessageRNW = (code, payload) => {
    /** code
     *
     * 0 : Logout event
     * 1 : After login give this object to the Native app {orgId: '', token: '', _id: ''}
     * 2 : For opening camera for capturing selfie for clock in/out with selfie clock in method : {}
     * 3 :
     * 4 :
     * 5 : This code will send current URL to HRMS application, when user on dashboard and press back button kill the app
     * 6 :
     * 7 : For background location tracking, this code will send the current location to HRMS application
     * 8 : For scanning face app open in HRMS application}
     * 9 :
     * 10 :
     * 11 :
     * 12 :
     * 13 : For QR scanner open in HRMS application {isQRScanner: true}
     * 14 :
     * 15 : For Quick links open in HRMS application {_id: "", link: "", isRead: "", name: ""}
     *
     * */
    window?.ReactNativeWebView?.postMessage(JSON.stringify({code, payload}));
    window?.webkit?.messageHandlers?.iosListener?.postMessage(JSON.stringify({code, payload}));
}

// checkRoute
export const checkRoute = (key, splitID, mainRoute, innerRoute, superID) => {
    if (key === ROUTES.myProfile && mainRoute) return true;
    else if (key === ROUTES.employeeProfile && !innerRoute && superID === splitID) return true;
    else if (key === ROUTES.employeeProfile && innerRoute && superID !== splitID) return true;

    if (key === 'my-profile' && mainRoute) return true;
    else if (key === 'employees' && !innerRoute && superID === splitID) return true;
    else if (key === 'employees' && innerRoute && superID !== splitID) return true;
    else return false
}

// checkBasicInfo
export const checkBasicInfo = (key, splitID, mainRoute, innerRoute, innerIDProofField, innerDateField, superID) => {
    if (key === 'my-profile' && mainRoute) return true;
    else if (key === 'employees' && !innerRoute && superID === splitID) return true;
    else if (key === 'employees' && innerRoute && superID !== splitID) return true;
    else if (key === 'employees' && !innerRoute && innerIDProofField) return true;
    else if (key === 'employees' && innerRoute && innerIDProofField) return true;
    else if (key === 'employees' && !innerRoute && innerDateField) return true;
    else if (key === 'employees' && innerRoute && innerDateField) return true;

    if (key === ROUTES.myProfile && mainRoute) return true;
    else if (key === ROUTES.employeeProfile && !innerRoute && superID === splitID) return true;
    else if (key === ROUTES.employeeProfile && innerRoute && superID !== splitID) return true;
    else if (key === ROUTES.employeeProfile && !innerRoute && innerIDProofField) return true;
    else if (key === ROUTES.employeeProfile && innerRoute && innerIDProofField) return true;
    else if (key === ROUTES.employeeProfile && !innerRoute && innerDateField) return true;
    else if (key === ROUTES.employeeProfile && innerRoute && innerDateField) return true;
    else return false
}

// getProfileUpdateValue
export const getProfileUpdateValue = (value, name, _ID_LV, _FS_LV) => {
    let str = value;
    if (name === 'relation') {
        str = getKeyName(_FS_LV, value)?.label;
    }
    if (name === 'dob') {
        str = dateFormatter({date: value, format: 'DD/MM/YYYY', timezone: APP.DEFAULT_TZ})
    }
    if (name === 'bloodGroup') {
        str = BLOOD_GROUP_LIST?.find(e => e?.value === value)?.label;
    }
    if (name === 'proofType') {
        str = _ID_LV?.find(e => e?.value === value)?.label;
    }
    if (name === 'isPrimary') {
        str = str ? 'Yes' : 'No'
    }
    if (name === 'isActive') {
        str = str ? 'Yes' : 'No'
    }
    if (name === 'isDeleted') {
        str = str ? 'Account Deleted' : '-'
    }
    if (name === 'isAddSameAsPermanent') {
        str = str ? 'Yes' : 'No'
    }
    if (name === 'bankId') {
        str = value?.name
    }
    if (name === 'countryType') {
        str = BANK_INFO_COUNTRY?.find(e => e?.type === value)?.label || "-";
    }
    if (name === 'accountType') {
        str = { saving: 'Savings', salary: 'Salary', current: 'Current', checking: 'Checking' }[value] || value;
    }
    return str || '--';
}

// getProfileUpdateLabel
export const getProfileUpdateLabel = (value, data) => {

    let str = (data?.type?.includes('IdProof') && value === 'attachments') ? '' : value?.split(/(?=[A-Z0-9])/).join(' ');
    if(BASIC_INFO_ACTIVITY_KEY?.includes(value) && data?.customFieldName){
        return (str = data?.customFieldName);
    }

    if (value === 'accHolderName') {
        str = 'Account Holder Name'
    }
    if (value === 'branch') {
        str = 'Bank Branch'
    }
    if (value === 'dob') {
        str = 'Date of Birth'
    }
    if (value === 'bornPlace') {
        str = 'Place of Birth'
    }
    if (value === 'isPrimary') {
        str = 'Primary Contact'
    }
    if (value === 'isAddSameAsPermanent') {
        str = 'Same as Current Address'
    }
    if (value === 'ifsc') {
        str = 'IFSC Code'
    }
    if (value === 'isActive') {
        str = 'Primary Account'
    }
    if (value === 'address1') {
        str = 'Current Address1'
    }
    if (value === 'address2') {
        str = 'Current Address2'
    }
    if (value === 'city') {
        str = 'Current City'
    }
    if (value === 'country') {
        str = 'Current Country'
    }
    if (value === 'province') {
        str = 'Current State'
    }
    if (value === 'zip') {
        str = 'Current Pin Code'
    }
    if (value === 'bankId') {
        str = 'Bank Name'
    }
    if (value === 'accountNo') {
        str = 'Account Number'
    }
    if (value === 'routingNo') {
        str = 'Routing Number'
    }
    if (value === 'iban') {
        str = 'IBAN Code'
    }
    if (value === 'swiftCode') {
        str = 'Swift Code'
    }
    if (value === 'bankCode') {
        str = 'Bank Code'
    }
    if (value === 'branchCode') {
        str = 'Branch Code'
    }
    if (value === 'accountType') {
        str = 'Account Type'
    }
    if (value === 'isDeleted') {
        str = 'Action'
    }
    if (value === 'permanentProvince') {
        str = 'permanent State'
    }
    if (value === 'permanentZip') {
        str = 'permanent Pin Code'
    }
    if (value === 'addIdProof') {
        str = 'Add Document'
    }
    if (value === 'updateIdProof') {
        str = 'Update Document'
    }
    if (value === 'deleteIdProof') {
        str = 'Delete Document'
    }
    if (value === 'deleteIdProofFiles') {
        str = 'Delete Document Attachment'
    }
    if (value === 'attachments' && data?.profileType === 'Bank Info') {
        str = 'Bank info Attachments'
    }
    if (value === 'countryType') {
        str = 'country Name'
    }

    return str;
}

// getStatusInfo
export const getStatusInfo = (status, type) => {
    return STATUS_OPTIONS.find(_ => _.value === status)?.[type];
}

// Any CLOCK_IN_APPROVAL request — time is read-only (shows the actual punch time), approval never modifies the timesheet
export const isClockInApprovalRequest = (obj) =>
    obj?.type === HRMS_STATUS.TIME_SHEET_REQUEST_TYPE.CLOCK_IN_APPROVAL;

// CLOCK_IN_APPROVAL request created because of geo-fencing restriction (requestType 4/5); old docs without the fields → false
export const isGeoApprovalRequest = (obj) =>
    isClockInApprovalRequest(obj) &&
    (obj?.requestTypes?.length ? obj.requestTypes : [obj?.requestType]).some((t) => [4, 5].includes(t));

// getStatusLabel
export const getStatusLabel = (obj) => {
    if (obj?.type === HRMS_STATUS.TIME_SHEET_REQUEST_TYPE.CLOCK_IN_APPROVAL) {
        if (isGeoApprovalRequest(obj)) return 'Geo-Fencing Restriction';
        return (obj?.isClockIn === false || (!obj?.inTime && obj?.outTime)) ? 'Clock Out Approval' : 'Clock In Approval';
    }
    if (obj?.timeSheetType === HRMS_STATUS.TIME_SHEET_TYPE.WORK) {
        if (obj?.inTime && obj?.outTime) return 'Clock In/Out'
        else if (obj?.inTime) return 'Clock In';
        else if (obj?.outTime) return 'Clock Out';
    } else if (obj?.timeSheetType === HRMS_STATUS.TIME_SHEET_TYPE.BREAK){
        if (obj?.inTime && obj?.outTime) return 'Break In/Out'
        else if (obj?.inTime) return 'Break In';
        else if (obj?.outTime) return 'Break Out';
    } else {
        if (obj?.inTime && obj?.outTime) return 'Clock In/Out'
        else if (obj?.inTime) return 'Clock In';
        else if (obj?.outTime) return 'Clock Out';
    }
}

// getLeaveType
export const getLeaveType = (item) => {
    if (item?.isNonWorkingDay && !item?.isLeave) {
        return 'Non Working Day'
    } else if (!item?.isLeave) {
        if (+item?.dayStatus === 2) {
            return 'Weekend'
        } else if (+item?.dayStatus === 1) {
            return 'Holiday'
        }
    } else if (item?.isLeave && item.type !== 4) {
        if (+item.type === 2) {
            return 'First Half'
        } else if (+item.type === 3) {
            return 'Second Half'
        } else if (+item.type === 5) {
            return 'Short Leave'
        } else {
            return 'Full Day'
        }
    } else if (item?.isLeave && item.type === 4) {
        if (+item?.dayStatus === 2) {
            return <td>Weekend</td>
        } else if (+item?.dayStatus === 1) {
            return <td>Holiday</td>
        } else {
            return <td> Total hours : {minutesToHm(item?.totalMin, true)} <br/> ({minutesTo12Hour(item?.startTime)} To {minutesTo12Hour(item?.endTime)}) </td>
        }
    }
}

// getSandwichRule
export const getSandwichRule = (leaveDays = []) => {
    let flag = []
    leaveDays.forEach(e => {
        if (e.isLeave === true && e.dayStatus !== 3 && e.dayStatus !== 4 && e.dayStatus !== 5) flag.push(e.dayStatus)
    })
    return flag
}

// getSandwichLeave
export const getSandwichLeave = (policyList, data) => {
    data = policyList?.find(_ => (_?._id === data?.sLeavePolicy && data?.sLeave)); // overide value
    if (data?.name) {
        return `(Sandwich Leave :- ${data?.name})`;
    } else return ""
}

// isValidShiftTiming
export const isValidShiftTiming = (weekend) => {
    let errors = false;

    let weekErrors = {};
    for (const [key, value] of Object.entries(weekend)) {
        let tempKey = key;
        weekErrors[HRMS_STATUS?.WEEK_NUMBER[tempKey]] = [];
        if (value) {
            if (value?.workStartAt === "" && value.workEndAt === "") {
                value.weekendDays.forEach(_ => {
                    if (_.workStartAt === "" && _.workEndAt === "") {
                        if (_?.weeknumber !== 6 && (_.type === HRMS_STATUS?.WEEKEND_TYPE?.FIRSTHALF || _.type === HRMS_STATUS?.WEEKEND_TYPE?.SECONDHALF || _.type === HRMS_STATUS?.WEEKEND_TYPE.WORK)) {
                            weekErrors[HRMS_STATUS?.WEEK_NUMBER[tempKey]].push(_?.weeknumber);
                            errors = {error: "Please enter valid time in Weekend Definition", code: 400};
                        }
                    }
                });
            } else {
                value.weekendDays.forEach(_ => {
                    if (_?.weeknumber !== 6 && (_.type === HRMS_STATUS?.WEEKEND_TYPE.FIRSTHALF && _.type === HRMS_STATUS?.WEEKEND_TYPE?.SECONDHALF)) {
                        if (_.workStartAt === "" && _.workEndAt === "") {
                            weekErrors[HRMS_STATUS.WEEK_NUMBER[tempKey]].push(_?.weeknumber);
                            errors = {error: "Please enter valid time in Weekend Definition", code: 400};
                        }
                    }
                })
            }
        }
    }
    let string = "";
    if (errors != false) {
        Object.entries(weekErrors)?.forEach((e) => {
            if (e[1].length > 0) {
                string = string.concat(`${e[0]} : `);
                e[1].forEach((_, i) => {
                    string = string.concat(`${_ + "" + HRMS_STATUS?.TAG_CODES[_ - 1]}, `);
                });
                string = string + '</br>'
            }
        });
        // string = string.slice(0, string.length - 2);
        string = string.concat(` is required OR configure working hours`)
        errors.error = string;
    }
    return errors;
}

// getReportTo
export const getReportTo = (list) => {
    let reportToOptions = [];
    list?.map(item => {
        reportToOptions.push({
            label: getName(item),
            value: item?._id,
            profileImage: item?.profileImage ? item?.profileImage : ''
        });
    });

    return reportToOptions?.length ? reportToOptions : null;
}

// checkModule
export const checkModule = (orgId, flag)=>{
    if(flag === 'profile-request') return window.location.origin?.includes("superworks.com") ? ALLOCATION.PROFILE_REQUEST.includes(orgId) : true
    if(flag === 'attendance-report') return (window.location.origin?.includes("superworks.com")) ? ALLOCATION.CUSTOM_SABBATICAL_LEAVE.includes(orgId) : true
    if(flag === 'shift') return (window.location.origin?.includes("superworks.com")) ? ALLOCATION.ATTENDANCE_TARGET_REPORT.includes(orgId) : true
    if(flag === 'monthly-billing-summary-report') return (window.location.origin?.includes("superworks.com")) ? ALLOCATION.MONTHLY_BILLING_SUMMARY_REPORT.includes(orgId) : true
}

// getDistance
export const getDistance = (lat1, lat2, lon1, lon2) => {
    // The math module contains a function
    // named toRadians which converts from
    // degrees to radians.
    lon1 = lon1 * Math.PI / 180;
    lon2 = lon2 * Math.PI / 180;
    lat1 = lat1 * Math.PI / 180;
    lat2 = lat2 * Math.PI / 180;

    // Haversine formula
    let dlon = lon2 - lon1;
    let dlat = lat2 - lat1;
    let a = Math.pow(Math.sin(dlat / 2), 2)
        + Math.cos(lat1) * Math.cos(lat2)
        * Math.pow(Math.sin(dlon / 2), 2);

    let c = 2 * Math.asin(Math.sqrt(a));

    // Radius of earth in kilometers. Use 3956
    // for miles
    let r = 6371;

    // calculate the result
    return (c * r);
}

// KMtoMeter
export const KMtoMeter = (km) => {
    return km * 1000;
}

// geocodeAddress
export const geocodeAddress = async (position) => {
    if (!window.google || !window.google.maps) {
        console.error('Google Maps JavaScript API is not loaded.');
        return '';
    }
    const options = {
        method: 'GET',
        url:`https://api.olamaps.io/places/v1/reverse-geocode?latlng=${position?.lat},${position?.lng}&api_key=${process.env.REACT_APP_OLA_MAP_API_KEY}`,
        headers: {'X-Request-Id': '', 'X-Correlation-Id': ''}
    };

    try {
        const { data } = await axios.request(options);

        const result = data?.results?.[0];
        if (!result) return null;


        // Extract the raw place name
        const name = result.name || '';


        // Safely extract individual structured admin parts from address_components
        const components = result.address_components || [];


        const getComponent = (type) => {
            const comp = components.find(c => c.types && c.types.includes(type));
            return comp ? comp.long_name : '';
        };


        const neighborhood = getComponent('neighborhood');
        const sublocality  = getComponent('sublocality');
        const locality     = getComponent('locality');
        const postalCode   = getComponent('postal_code');


        // Filter out empty strings to prevent double-commas or trailing separators
        const addressParts = [name, neighborhood, sublocality, locality]
            .map(part => part.trim())
            .filter(part => part.length > 0);


        // Join the primary strings together with a comma
        let cleanAddress = addressParts.join(', ');


        // Append the postal code/PIN at the end cleanly
        if (postalCode) {
            cleanAddress += - `${postalCode.trim()}`;
        }


        return cleanAddress || result?.formatted_address || "";
    } catch (error) {
        console.error(error);
        return ""
    }

    // const geocoder = new window.google.maps.Geocoder();

    // return new Promise((resolve) => {
    //     geocoder.geocode({ location: position }, (results, status) => {
    //         if (status === 'OK' && results?.length) {
    //             resolve(results[0].formatted_address);
    //         } else {
    //             console.error(`Geocoding failed: ${status}`);
    //             resolve('');
    //         }
    //     });
    // });
};

// handleCommentReaction
export const handleCommentReaction = (commentList, data, _id) => {
    return commentList?.map(comment => {
        if (comment?._id === data?.commentId) {
            let reactionData = [...(comment.reactionData || [])]
            const index = reactionData.findIndex(e => e._id === data.reaction)

            if (index !== -1) {
                if (data.actionBy === _id) reactionData[index].isLike = data.revert ? 0 : 1
                const count = reactionData[index].count + (data.revert ? -1 : 1)
                if (count === 0) reactionData.splice(index, 1)
                else {
                    reactionData[index].count = count
                    const userIds = reactionData[index]?.userIds || []
                    if (data.revert) userIds.splice(userIds.indexOf(data.actionBy), 1);
                    else userIds.push(data.actionBy)
                    reactionData[index].userIds = userIds
                }
            } else if (!data.revert) {
                reactionData.push({
                    _id: data?.reaction,
                    isLike: data.actionBy === _id ? 1 : 0,
                    count: 1,
                    userIds: [data.actionBy]
                })
            }

            comment.reactionData = [...reactionData]
        }
        return comment;
    });
}

// handleCommentUser
export const handleCommentUser = (dispatch, arr) => {

    let userIds = []

    arr.map(comment => {
        userIds.push(comment?.user)
        comment.reactionData.map(e => {
            userIds = [...userIds, ...e.userIds]
        })
    })
    userIds = userIds.filter(e => e)
    if (userIds.length !== 0) dispatch({
        type: 'FIND_MASTER_DATA_LIST',
        payload: {findUserIdsList: [...new Set(userIds)]}
    })
}

// generateObjectId
export const generateObjectId = () => {
    return Math.floor(Date.now() / 1000)?.toString(16) + 'xxxxxxxxxxxxxxxx'?.replace(/[x]/g, () => (Math.random() * 16 | 0)?.toString(16));
};

// getActualField
export const getActualField = (formValues, key) => {
    let result = "NOT_VALUE_MATCH";

    // Case 1: Array of objects
    if (Array.isArray(formValues) && formValues?.every(item => typeof item === "object" && !Array.isArray(item) && item !== null)) {
        result = formValues.find(item => item.key === key);
        return result || "NOT_VALUE_MATCH";
    }

    // Case 2: Array of arrays — search recursively inside each
    if (Array.isArray(formValues) && formValues?.every(item => Array.isArray(item))) {
        for (const innerArray of formValues) {
            result = getActualField(innerArray, key);
            if (result !== "NOT_VALUE_MATCH") return result;
        }
    }
    return result;
};

// getFields
export const getFields = (keyList = [], allFields) => {
    const formValues = allFields ? cloneDeep(allFields) : null;
    if (logEnable) console.log(`===>>> getFields >> Params`, keyList, allFields)
    if (!formValues) {
        return [];
    }
    // Stores found objects for the requested keys.
    const results = [];
    // Keeps track of keys found to avoid duplicates.
    const visitedKeys = new Set();

    // Helper: Check if a value is a plain object.
    const isObject = (val) =>
        typeof val === "object" && val !== null && !Array.isArray(val);

    // Helper: Check if an array is entirely composed of objects.
    const isArrayOfObjects = (arr) =>
        Array.isArray(arr) && arr.every(item => isObject(item));

    // Recursive search function.
    const search = (input) => {
        // Ensure input is defined before proceeding.
        if (input == null) return;

        if (Array.isArray(input)) {
            // If it's an array of objects, try to find each requested key.
            if (isArrayOfObjects(input)) {
                keyList?.forEach(key => {
                    if (!visitedKeys.has(key)) {
                        // Use optional chaining to safely access properties.
                        const found = input?.find(item => item?.key === key);
                        if (found) {
                            results.push(found);
                            visitedKeys.add(key);
                        }
                    }
                });
            } else {
                // Otherwise, iterate through the array and search each element.
                for (const item of input) {
                    search(item);
                }
            }
        } else if (isObject(input)) {
            // If it's an object, search through its values.
            for (const val of Object.values(input || {})) {
                search(val);
            }
        }
    };

    // Kick off the recursive search.
    search(formValues);
    if (logEnable) console.log(`===>>> getFields >> results`, results)
    return results.length ? results : [];
};

// conditionEvaluate
export const conditionEvaluate = (condition, allFields) => {
    const {key, operator, value: expected, compareKey} = condition || {};
    if(logEnable) console.log("===>>> conditionEvaluate Params >> condition", condition, "\nallFields", allFields, "\nkey",key, "\noperator", operator)

    let actualField = getFields([key], allFields)
    if (actualField) actualField = actualField?.find(_ => _?.key === key);
    if(logEnable) console.log("===>>> conditionEvaluate >> actualField", actualField)
    let actual = actualField?.value;
    if(compareKey && actualField){
        actual = actualField[compareKey];
    }

    switch (operator) {
        case OPERATORS.EQUALS:
            return isEqual(actual, expected);

        case OPERATORS.NOT_EQUALS:
            return !isEqual(actual, expected);

        case OPERATORS.INCLUDES:
            return Array.isArray(actual) && actual.includes(expected);

        case OPERATORS.NOT_INCLUDES:
            return Array.isArray(actual) && !actual.includes(expected);

        case OPERATORS.GREATER_THAN:
            return actual > expected;

        case OPERATORS.LESS_THAN:
            return actual < expected;

        case OPERATORS.GREATER_EQUAL:
            return actual >= expected;

        case OPERATORS.LESS_EQUAL:
            return actual <= expected;

        case OPERATORS.CONTAINS:
            return actual?.includes?.(expected);

        case OPERATORS.EMPTY:
            return !isValueTruthy(actual);

        case OPERATORS.NOT_EMPTY:
            return isValueTruthy(actual);

        case OPERATORS.DATE_BEFORE:
            return isBefore(new Date(actual), new Date(expected));

        case OPERATORS.DATE_AFTER:
            return isAfter(new Date(actual), new Date(expected));

        case OPERATORS.IS_TRUTHY:
            return isValueTruthy(actual);
        case OPERATORS.IS_NOT_TRUTHY:
            return !isValueTruthy(actual);
        default:
            console.log("== OPERATOR IS NOT DEFINED ==")
            return null;
    }
};

// checkCondition
export const checkCondition = ({logic = 'AND', conditions = [], allFields = []}) => {
    if(logEnable) console.log("===>>> checkCondition >> params", {logic, conditions, allFields})
    const results = conditions?.map(cond => conditionEvaluate(cond, allFields));
    if(logEnable) console.log("===>>> checkCondition >> results", results)
    return logic === 'OR' ? results?.some(Boolean) : results?.every(Boolean);
};

// applyUpdates
export const applyUpdates = (updatesList = [], data = [], dispatch = null) => {
    // Step 1: Build a map of updates for fast lookup
    const updatesMap = new Map();
    for (const item of updatesList) {
        if (item?.key && typeof item?.updateData === 'object') {
            updatesMap.set(item.key, item.updateData);
        }
    }

    // Helper: Check if value is a plain object
    const isObject = (val) =>
        typeof val === 'object' && val !== null && !Array.isArray(val);

    // Helper: Check if the object is a valid target for update
    const isUpdatableObject = (obj) =>
        isObject(obj) &&
        typeof obj.key === 'string' &&
        typeof obj._id === 'string';

    /**
     * Recursively update matching nested items
     * @param {any} input
     * @returns {any}
     */
    const recursiveUpdate = (input) => {
        if (Array.isArray(input)) {
            return input.map((item) => recursiveUpdate(item));
        } else if (isObject(input)) {
            // If this object matches update conditions, apply update
            if (isUpdatableObject(input) && updatesMap.has(input.key)) {
                const updates = updatesMap.get(input.key);
                const updated = {...input};

                // Merge each update property
                for (const [updateKey, updateValue] of Object.entries(updates)) {
                    updated[updateKey] = updateValue;
                    if(logEnable) console.log(`===>>> applyUpdates ========`, "\nupdateKey",updateKey, "\nupdateValue", updateValue, "\nfield",updated)
                }
                return updated;
            }

            // Otherwise, recursively update children
            const result = {};
            for (const [key, value] of Object.entries(input || {})) {
                result[key] = recursiveUpdate(value);
            }
            return result;
        }

        // Return primitives unchanged
        return input;
    };

    // Step 2: Deep clone the original data
    const cloned = cloneDeep(data);

    // Step 3: Apply updates recursively
    const updatedData = recursiveUpdate(cloned);

    if(typeof dispatch === 'function') {
        dispatch({type: "SET_ALL_FIELDS_CHANGE", payload: updatedData, isChange: true});
    }
    return updatedData;
}

// prepareSaveData
export const prepareSaveData = (source = []) => {
    /**
     * This is Master Fn, You can modify it if you want to change data before sending it to the backend
     * */
    let customFieldsData = {};
    let defaultFieldsData = {};

    if(Array?.isArray(source) && source?.length > 0){
        source?.forEach(field => {
            if (field?.type === CUSTOM_FIELD_TYPE.CONTACT) {
                if(field?.defaultKey){
                    defaultFieldsData[`${field?.defaultKey}CC`] = field?.[`${field?.defaultKey}CC`] || null;
                    defaultFieldsData[field?.defaultKey] = field?.value || null
                }else {
                    customFieldsData[`${field?.key}CC`] = field?.[`${field?.key}CC`] || null;
                    customFieldsData[field?.key] = field?.value || null
                }
            }else if(field?.type === CUSTOM_FIELD_TYPE.DATE){
                if(field?.defaultKey){
                    defaultFieldsData[field?.defaultKey] = localToIST(field?.value) || null
                }else {
                    customFieldsData[field?.key] = localToIST(field?.value) || null
                }
            }else {
                if(field?.defaultKey){
                    defaultFieldsData[field?.defaultKey] = field?.value || null
                }else {
                    customFieldsData[field?.key] = field?.value || null
                }
            }

            /** By Default Value set "null" but for Some exceptional case handle below **/
            if([D_FIELDS?.lastName_basic]?.includes(field?.key)){
                if(field?.defaultKey){
                    defaultFieldsData[field?.defaultKey] = field?.value || ""
                }
            }else if([D_FIELDS?.isPhysicalHandicap_basic]?.includes(field?.key)){
                if(field?.defaultKey){
                    defaultFieldsData[field?.defaultKey] = field?.value || false;
                }
            }
        })
    }

    return {customFieldsData, defaultFieldsData}
}

// formatKeyName
export const formatKeyName = (key) => {
    return key
        .replace(/([a-z])([A-Z])/g, "$1 $2") // camelCase to space
        .replace(/_/g, " ") // snake_case to space
        .replace(/\b\w/g, (char) => char.toUpperCase()); // Capitalize words
}

/**
 * "Create Job" prompt shown after a resignation auto-requisition is created.
 *
 * The backend flags `showJobPrompt` on the create response; when set, the user is asked
 * whether the job should be created right away:
 *   - Yes → onCreate({_id, isCreated: true}) → job is created
 *   - No  → onCreate({_id})                  → saved as a draft job
 * X / outside click does nothing (`flag` is only 'button' for the No button).
 *
 * UI only — the caller owns the socket emit and its response, because the framework
 * never talks to a module's store or socket.
 *
 * @param {object} res     parsed socket response for the auto-requisition create event
 * @param {object} opts    { alert: useUAlert() instance, onCreate: (payload) => void }
 * @returns {boolean} true if the prompt was shown
 */
export const showCreateJobPrompt = (res, {alert, onCreate} = {}) => {
    if (!alert || res?.data?.showJobPrompt !== true || !res?.data?.result?.requisitionId) return false;
    const _id = res.data.result.requisitionId;

    alert.show({
        type: 'success',
        icon: <Icon.BriefCase fill={COLOR.u_blue} size={30}/>,
        title: 'Create Job',
        description: `<p>Do you want to create this job now?<br /><span class="u_small-font u_pastelred">if you select Without Setup it will save as draft in Job section</span></p>`,
        submit: 'With Setup',
        close: 'Without Setup',
        onSubmit: () => onCreate?.({_id, isCreated: true}),
        onClose: (e, flag) => {
            if (flag === 'button') onCreate?.({_id});
        },
    });
    return true;
};

/**
 * Calculates final mobile / phone country code based on location, branch, country list, and fallbacks.
 *
 * @param {object} params
 * @param {string} [params.locationCC] - Primary phone CC from location API fetch
 * @param {string} [params.locId] - Location ID
 * @param {string} [params.location] - Alias for locId
 * @param {Array}  [params.branchList] - Branch/Location raw array (_LC_RAW)
 * @param {Array}  [params.branchOptions] - Branch label-value options array (_LC_LV)
 * @param {Array}  [params.countryList] - Country list array (_COUNTRY_LIST)
 * @param {string} [params.phoneNumberCC] - Phone CC from allPayloads / user
 * @param {string} [params.defaultPhoneCC] - Fallback phone CC
 * @param {object|string} [params.selectedCountry] - Selected country object/label
 * @returns {string} resolved mobile country code
 */
export const mobileCode = ({
    locationCC = '',
    locId = '',
    location = '',
    branchList = [],
    branchOptions = [],
    countryList = [],
    phoneNumberCC = '',
    defaultPhoneCC = '',
    selectedCountry = null,
} = {}) => {
    const targetLocId = locId || location;
    let selectedCC = '';
    let branchCC = '';

    let locationCountryName = selectedCountry?.label || selectedCountry?.name || (typeof selectedCountry === 'string' && selectedCountry.length !== 24 ? selectedCountry : '');

    if (locationCountryName) {
        selectedCC = CONSTANT?.COUNTRY_CODE?.find(
            c => c.name?.toLowerCase() === locationCountryName?.toLowerCase() || c.code?.toLowerCase() === locationCountryName?.toLowerCase()
        )?.code || '';
    }

    if (!selectedCC) {
        const branchObj = (branchList || [])?.find(l => l?._id === targetLocId || l?.value === targetLocId);

        if (branchObj?.primaryPhoneCC) {
            branchCC = branchObj.primaryPhoneCC;
        } else {
            let branchCountryName = '';
            if (branchObj?.country) {
                const countryObj = (countryList || [])?.find(c => c?._id === branchObj.country);
                if (countryObj?.name) {
                    branchCountryName = countryObj.name;
                } else if (typeof branchObj.country === 'string' && branchObj.country.length !== 24) {
                    branchCountryName = branchObj.country;
                }
            }
            if (!branchCountryName) {
                branchCountryName = branchObj?.name || branchObj?.branchName || (branchOptions || [])?.find(l => l?.value === targetLocId)?.label || '';
            }
            if (branchCountryName) {
                branchCC = CONSTANT?.COUNTRY_CODE?.find(
                    c => c.name?.toLowerCase() === branchCountryName?.toLowerCase()
                )?.code || '';
            }
        }
    }

    return selectedCC || locationCC || branchCC || phoneNumberCC || defaultPhoneCC || '';
};


export const buildGeoPolicyPayload = (geoGroupPolicy, geoFencingpolicy) => {
    const toId = (e) => (isString(e) ? e : (e?.value || e?._id || null));
    const toIds = (v) => (isArray(v) ? v : [v]).map(toId).filter(Boolean);
    const groupIds = toIds(geoGroupPolicy);
    const policyIds = toIds(geoFencingpolicy);
    const result = {};
    if (groupIds.length) result.geoGroupPolicy = groupIds;
    if (policyIds.length) result.geoFencingpolicy = policyIds;
    return result;
};

export const resolveRestrictPfWage = (restrictWage, asOfDate) => {
    if (restrictWage === null || restrictWage === undefined) return 0;
    if (!Array.isArray(restrictWage)) return Number(restrictWage) || 0;
    if (!restrictWage.length) return 0;

    const target = new Date(asOfDate || Date.now()).getTime();
    const sorted = [...restrictWage].sort((a, b) => new Date(a.date) - new Date(b.date));

    let active = sorted[0];
    for (const row of sorted) {
        if (new Date(row.date).getTime() <= target) active = row;
        else break;
    }
    return Number(active?.amount) || 0;
};